import React, { useEffect, useLayoutEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ColorModeContext, DARK, LIGHT } from 'app/utils/ColorContext';
import { useDispatch, useSelector } from 'react-redux';
import AppRoutes from 'AppRoutes';
import AppToast from 'app/components/shared/AppToast';
import { clearMessage } from 'redux/common/messageSlice';
import { Copyright } from 'components/copyright/copyright';
import { getCopyrightVisibility } from 'redux/auth/authSlice';
import { getBreakpoint } from 'utils/panel.util';
import { changeBreakpoint } from 'redux/dashboard/dasbhoardSlice';
import { grey } from '@mui/material/colors';
import useSessionTimeout from 'hooks/useSessionTimeout';

function App() {
    const dispatch = useDispatch();
    useSessionTimeout();
    const [mode, setMode] = React.useState('dark');
    const { message, severity } = useSelector(state => state.toast);
    const showCopyright = useSelector(getCopyrightVisibility);

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode(prevMode => (prevMode === LIGHT ? DARK : LIGHT));
            },
            mode,
        }),
        [mode],
    );

    const theme = React.useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        ...grey,
                        ...(mode === 'dark' && {
                            main: grey.A100,
                        }),
                        ...(mode === 'light' && {
                            main: grey[900],
                        }),
                    },
                    ...(mode === 'dark' && {
                        background: {
                            default: '#1b2232',
                            paper: '#1b2232',
                        },
                    }),
                },
            }),
        [mode],
    );

    useEffect(() => {
        const storage = localStorage.getItem('mode') || DARK;
        setMode(storage);
    }, []);

    const handleToastClose = () => {
        dispatch(clearMessage());
    };

    useLayoutEffect(() => {
        const width = window.innerWidth;
        const screenBreakpoint = getBreakpoint(width);
        dispatch(changeBreakpoint(screenBreakpoint));
    }, []);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                {message && (
                    <AppToast
                        open
                        message={message}
                        severity={severity}
                        handleClose={handleToastClose}
                    />
                )}
                <AppRoutes />
                {showCopyright && <Copyright />}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
